import axios from "axios";
import { BaseApiUrl } from "./base";
import { normalizeDeptId, treeNodeDeptId } from "../utils/deptCascaderProps";

const base = BaseApiUrl;

// ──────────────────────────────────────────────
//  部门树工具（五育评价页面共用）
// ──────────────────────────────────────────────

/** 获取部门树节点组织类型（小写） */
export function getDeptOrgType(dataOrNode) {
  const data = dataOrNode && dataOrNode.data ? dataOrNode.data : dataOrNode;
  if (!data) return "";
  return String(data.DeptOrgType != null ? data.DeptOrgType : data.deptOrgType || "").toLowerCase();
}

/** 是否学校根节点 */
export function isSchoolDeptNode(dataOrNode) {
  const t = getDeptOrgType(dataOrNode);
  return t === "school" || t === "affiliateschool";
}

/** 是否行政班节点 */
export function isClassDeptNode(node) {
  return getDeptOrgType(node) === "class";
}

/** 从部门树节点向上解析学校根 Id（字符串，避免雪花精度丢失）。最后修改：2026-10-18 */
export function resolveSchoolIdFromTreeNode(node, fallbackId) {
  if (node && node.data && isSchoolDeptNode(node)) {
    return treeNodeDeptId(node.data);
  }
  let n = node && node.parent;
  while (n && n.data) {
    if (isSchoolDeptNode(n)) {
      return treeNodeDeptId(n.data);
    }
    n = n.parent;
  }
  return normalizeDeptId(fallbackId);
}

/** 收集节点下所有行政班 Id（含自身若为 class；字符串）。最后修改：2026-10-18 */
export function collectClassIdsFromTreeNode(node) {
  const ids = [];
  const seen = new Set();
  const walk = (n) => {
    if (!n) return;
    const data = n.data || n;
    if (isClassDeptNode({ data: data })) {
      const id = treeNodeDeptId(data);
      if (id && !seen.has(id)) {
        seen.add(id);
        ids.push(id);
      }
    }
    const children = n.childNodes || [];
    for (let i = 0; i < children.length; i++) {
      walk(children[i]);
    }
  };
  walk(node);
  return ids;
}

// ──────────────────────────────────────────────
//  成绩项配置
// ──────────────────────────────────────────────

/** 获取一级类别树（含二级及成绩项数量）；gradeId 可选，按适用年级过滤项数 */
export const getCategoryTree = (departmentId, gradeId = 0) =>
  axios.get(`${base}/api/FiveEducationAdmin/GetCategoryTree`, {
    params: { departmentId, gradeId: gradeId || undefined },
  });

/** 保存一级类别（新增/更新） */
export const saveCategoryL1 = (data) =>
  axios.post(`${base}/api/FiveEducationAdmin/SaveCategoryL1`, data);

/** 删除一级类别 */
export const deleteCategoryL1 = (id) =>
  axios.delete(`${base}/api/FiveEducationAdmin/DeleteCategoryL1`, { params: { id } });

/** 保存二级类别（新增/更新） */
export const saveCategoryL2 = (data) =>
  axios.post(`${base}/api/FiveEducationAdmin/SaveCategoryL2`, data);

/** 删除二级类别 */
export const deleteCategoryL2 = (id) =>
  axios.delete(`${base}/api/FiveEducationAdmin/DeleteCategoryL2`, { params: { id } });

/** 获取三级成绩项列表（分页）；gradeId 可选，按适用年级过滤 */
export const getScoreItems = (categoryL2Id, gradeId = 0, page = 1, pageSize = 10) =>
  axios.get(`${base}/api/FiveEducationAdmin/GetScoreItems`, {
    params: {
      categoryL2Id,
      gradeId: gradeId || undefined,
      page,
      pageSize,
    },
  });

/** 保存三级成绩项（新增/更新） */
export const saveScoreItem = (data) =>
  axios.post(`${base}/api/FiveEducationAdmin/SaveScoreItem`, data);

/** 删除三级成绩项 */
export const deleteScoreItem = (id) =>
  axios.delete(`${base}/api/FiveEducationAdmin/DeleteScoreItem`, { params: { id } });

// ──────────────────────────────────────────────
//  班级评分情况（无批次，按学校查所有班级最新轮次）
// ──────────────────────────────────────────────

/** 获取当前自然月周期键 yyyyMM */
export function getCurrentMonthPeriodKey() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return "" + y + m;
}

/** 获取学校下各班级指定周期评分进度 */
export const getClassRatingStatusList = (schoolDepartmentId, periodType, periodKey) =>
  axios.get(`${base}/api/FiveEducationAdmin/GetClassRatingStatusList`, {
    params: {
      schoolDepartmentId,
      periodType: periodType || undefined,
      periodKey: periodKey || undefined,
    },
  });

/** 获取班级可选评价周期列表 */
export const getClassPeriodOptions = (classId) =>
  axios.get(`${base}/api/FiveEducationAdmin/GetClassPeriodOptions`, {
    params: { classId },
  });

/** 获取指定班级指定轮次的任教教师提交明细 */
export const getTeacherSubmitDetail = (classId, roundNo) =>
  axios.get(`${base}/api/FiveEducationAdmin/GetTeacherSubmitDetail`, {
    params: { classId, roundNo },
  });

/** 获取指定班级全部历史轮次及教师参与情况 */
export const getClassRoundHistory = (classId) =>
  axios.get(`${base}/api/FiveEducationAdmin/GetClassRoundHistory`, {
    params: { classId },
  });

/** 导出班级评分情况列表 Excel */
export const exportClassRatingStatusList = (schoolDepartmentId, classIds, periodType, periodKey) =>
  axios.get(`${base}/api/FiveEducationAdmin/ExportClassRatingStatusList`, {
    params: {
      schoolDepartmentId,
      classIds: classIds || undefined,
      periodType: periodType || undefined,
      periodKey: periodKey || undefined,
    },
    responseType: "blob",
  });

/** 导出班级历史轮次 Excel */
export const exportClassRoundHistory = (classId) =>
  axios.get(`${base}/api/FiveEducationAdmin/ExportClassRoundHistory`, {
    params: { classId },
    responseType: "blob",
  });

/** 导出单轮评分概况及教师明细 Excel */
export const exportClassRoundDetail = (classId, roundNo) =>
  axios.get(`${base}/api/FiveEducationAdmin/ExportClassRoundDetail`, {
    params: { classId, roundNo },
    responseType: "blob",
  });

/** 下载 blob 为 Excel */
export function downloadBlobExcel(res, filePrefix) {
  const blob = new Blob([res.data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filePrefix + "_" + new Date().getTime() + ".xlsx";
  link.click();
  window.URL.revokeObjectURL(url);
}

// ──────────────────────────────────────────────
//  数据看板（roundNo=0 时自动取最新已结束轮次）
// ──────────────────────────────────────────────

/** 获取看板学生星级列表（分页） */
export const getDashboardStudentList = (classId, roundNo = 0, pageIndex = 1, pageSize = 20) =>
  axios.get(`${base}/api/FiveEducationAdmin/GetDashboardStudentList`, {
    params: { classId, roundNo, pageIndex, pageSize },
  });

/** 获取单个学生评星详情 */
export const getStudentStarDetail = (studentId, roundNo = 0) =>
  axios.get(`${base}/api/FiveEducationAdmin/GetStudentStarDetail`, {
    params: { studentId, roundNo },
  });

/** 获取班级排行榜（全量，教师端等场景） */
export const getClassRanking = (classId, roundNo = 0) =>
  axios.get(`${base}/api/FiveEducationAdmin/GetClassRanking`, {
    params: { classId, roundNo },
  });

/** 获取班级排行榜（分页，数据看板用；keyword 匹配姓名或卡号） */
export const getClassRankingPage = (
  classId,
  roundNo = 0,
  pageIndex = 1,
  pageSize = 10,
  keyword = "",
  periodType,
  periodKey
) =>
  axios.get(`${base}/api/FiveEducationAdmin/GetClassRankingPage`, {
    params: {
      classId,
      roundNo,
      pageIndex,
      pageSize,
      keyword: keyword || undefined,
      periodType: periodType || undefined,
      periodKey: periodKey || undefined,
    },
  });

/** 获取学生某一级类别成长趋势（历次已结束轮次星级） */
export const getGrowthTrend = (studentId, categoryL1Id) =>
  axios.get(`${base}/api/FiveEducationAdmin/GetGrowthTrend`, {
    params: { studentId, categoryL1Id },
  });

/** 获取学生某二级类别成长趋势（历次已结束轮次星级） */
export const getGrowthTrendByL2 = (studentId, categoryL2Id) =>
  axios.get(`${base}/api/FiveEducationAdmin/GetGrowthTrendByL2`, {
    params: { studentId, categoryL2Id },
  });
